import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import { translations } from './translations';
import './App.css';

function Home() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { language, toggleLanguage } = useLanguage();
  const t = translations[language];

  const services = [
    {
      title: t.processAutomation,
      description: t.processAutomationDesc,
      icon: '⚙️'
    },
    {
      title: t.integrations,
      description: t.integrationsDesc,
      icon: '🔗'
    },
    {
      title: t.aiAssistants,
      description: t.aiAssistantsDesc,
      icon: '🤖'
    },
    {
      title: t.marketingService,
      description: t.marketingServiceDesc,
      icon: '📈'
    },
    {
      title: t.measurableOps,
      description: t.measurableOpsDesc,
      icon: '📊'
    }
  ];

  const steps = [
    {
      title: t.step1Title,
      detail: t.step1Detail
    },
    {
      title: t.step2Title,
      detail: t.step2Detail
    },
    {
      title: t.step3Title,
      detail: t.step3Detail
    },
    {
      title: t.step4Title,
      detail: t.step4Detail
    }
  ];

  return (
    <div className="page">
      <header className="topbar">
        <Link to="/" className="brand">
          <img src="/velyntor_logo-removebg-preview.png" alt="Velyntor" className="brand-logo" />
        </Link>
        <div className="brand-center">VELYNTOR</div>
        <button
          className={`menu-toggle ${menuOpen ? 'open' : ''}`}
          aria-label="Öppna meny"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((prev) => !prev)}
        >
          <span />
          <span />
          <span />
        </button>
        <div className="topbar-right">
          <nav className={`nav ${menuOpen ? 'open' : ''}`}>
            <Link to="/" onClick={() => setMenuOpen(false)}>
              {t.automation}
            </Link>
            <Link to="/marknadsforing" onClick={() => setMenuOpen(false)}>
              {t.marketing}
            </Link>
            <Link to="/webbutveckling" onClick={() => setMenuOpen(false)}>
              {t.webDevelopment}
            </Link>
            <Link to="/om-oss" onClick={() => setMenuOpen(false)}>
              {t.about}
            </Link>
            <Link to="/kontakt" onClick={() => setMenuOpen(false)}>
              {t.contact}
            </Link>
            <Link to="/kontakt" className="ghost-btn mobile-only" onClick={() => setMenuOpen(false)}>
              {t.bookMeeting}
            </Link>
            <button className="language-toggle mobile-only" onClick={toggleLanguage}>
              <img 
                src="https://flagcdn.com/w20/se.png" 
                alt="Svenska" 
                className={`flag-icon ${language === 'sv' ? 'active' : ''}`}
              />
              <span>/</span>
              <img 
                src="https://flagcdn.com/w20/gb.png" 
                alt="English" 
                className={`flag-icon ${language === 'en' ? 'active' : ''}`}
              />
            </button>
          </nav>
          <button className="language-toggle desktop-only" onClick={toggleLanguage}>
            <img 
              src="https://flagcdn.com/w20/se.png" 
              alt="Svenska" 
              className={`flag-icon ${language === 'sv' ? 'active' : ''}`}
            />
            <span>/</span>
            <img 
              src="https://flagcdn.com/w20/gb.png" 
              alt="English" 
              className={`flag-icon ${language === 'en' ? 'active' : ''}`}
            />
          </button>
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="hero-text">
            <p className="eyebrow">{t.eyebrow}</p>
            <h1>{t.heroTitle}</h1>
            <p className="lede">
              {t.heroText}
            </p>
            <div style={{ marginTop: '24px' }}>
              <Link to="/kontakt" className="primary-btn">
                {t.bookMeeting}
              </Link>
            </div>
          </div>
          <div className="hero-card">
            <div className="card-title">{t.automationStatus}</div>
            <div className="metric">
              <div className="metric-value">80%</div>
              <div className="metric-label">{t.reductionLabel}</div>
            </div>
            <div className="metric">
              <div className="metric-value">300%</div>
              <div className="metric-label">{t.revenueLabel}</div>
            </div>
            <div className="metric">
              <div className="metric-value">99.9%</div>
              <div className="metric-label">{t.availabilityLabel}</div>
            </div>
            <p className="card-foot">{t.cardFooter}</p>
          </div>
        </section>

        <section id="services" className="section services">
          <div className="section-heading">
            <p className="eyebrow">{t.servicesEyebrow}</p>
            <h2>{t.servicesTitle}</h2>
            <p className="section-sub">
              {t.servicesSubtitle}
            </p>
          </div>
          <div className="grid">
            {services.map((service) => (
              <div key={service.title} className="card">
                <div className="card-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="section alt platform-section">
          <div className="section-heading">
            <p className="eyebrow">{t.platformEyebrow}</p>
            <h2>{t.platformTitle}</h2>
            <p className="section-sub">
              {t.platformSubtitle}
            </p>
          </div>
          <div className="pill-row">
            <span className="pill">{t.eventDrivenDesc}</span>
            <span className="pill">{t.apiFirstDesc}</span>
            <span className="pill">{t.cicdDesc}</span>
            <span className="pill">{t.observabilityDesc}</span>
            <span className="pill">{t.llmAgentsDesc}</span>
            <span className="pill">{t.securityDesc}</span>
          </div>
        </section>

        <section id="process" className="section">
          <div className="section-heading">
            <p className="eyebrow">{t.processEyebrow}</p>
            <h2>{t.processTitle}</h2>
          </div>
          <div className="steps">
            {steps.map((step) => (
              <div key={step.title} className="step">
                <div className="step-dot" />
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section id="contact" className="section cta">
          <div>
            <p className="eyebrow">{t.contact}</p>
            <h2>{t.ctaTitle2}</h2>
            <p className="section-sub">
              {t.ctaSubtitle}
            </p>
          </div>
          <div className="cta-actions">
            <Link to="/kontakt" className="primary-btn" style={{ textDecoration: 'none', display: 'inline-block' }}>{t.bookMeeting}</Link>
            <a href="mailto:info@velyntor.se" className="secondary-btn" style={{ textDecoration: 'none', display: 'inline-block' }}>{t.email}</a>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>{t.footerContact}</h4>
            <p style={{ marginTop: '8px' }}>
              <a href="mailto:info@velyntor.se">{t.email}</a>
            </p>
            <p style={{ marginTop: '4px' }}>
              <a href="tel:+46700252952">+46 70 025 29 52</a>
            </p>
            <p style={{ marginTop: '4px' }}>
              <a href="https://linkedin.com/company/techblaze-ab/" target="_blank" rel="noreferrer">
                LinkedIn
              </a>
            </p>
          </div>
          <div className="footer-section">
            <h4>{t.footerPages}</h4>
            <div className="footer-links">
              <Link to="/">{t.automation}</Link>
              <Link to="/marknadsforing">{t.marketing}</Link>
              <Link to="/webbutveckling">{t.webDevelopment}</Link>
              <Link to="/om-oss">{t.about}</Link>
              <Link to="/kontakt">{t.contact}</Link>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <div>© {new Date().getFullYear()} Velyntor. {t.footerRights}</div>
        </div>
      </footer>
    </div>
  );
}

export default Home;

